import { useKitbagAuth } from '@statsbomb/kitbag-auth';
import { Alert, ButtonIcon, Grid } from '@statsbomb/kitbag-components';
import { _VideoPlayer } from '@statsbomb/video-client-js';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { styled } from 'styled-components';
import {
  approvalInProgressAtom,
  editingQAChecklistAtom,
  metadataIsUpdatingAtom,
  sourceDataIsUpdatingAtom,
} from '../../../atoms/approval';
import {
  messageAwaitingApprovalActionsAtom,
  messageMoveVideoActionAtom,
} from '../../../atoms/messages';
import { editingSourceDataAtom } from '../../../atoms/review';
import { selectedSourceDataAtom } from '../../../atoms/selectedSourceData';
import useApi, { API_ENDPOINTS } from '../../../hooks/api';
import useVideoListings from '../../../hooks/videoListings';
import { qaDataPropTypes } from '../../../serialisers/qualityAsssurance';
import { sourceSerialiser } from '../../../serialisers/sourceData';
import themeHelper from '../../../utils/themeHelper';
import { timestampAsMicroseconds } from '../../../utils/time';
import Confirm from '../../Confirm';
import OffsetData from '../../OffsetData';
import { VALUE_IDENTIFYING_NOT_SHOWN } from '../../OffsetData/OffsetData';
import QACheckConfirmation from '../../QACheckConfirmation';
import QAChecklist from '../../QAChecklist';
import { QAInfoView } from '../../QAInfoView/QAInfoView';
import { SourceData } from '../../SourceData/SourceData';
import { FOOTBALL_IDENTIFIER } from '../../Sport/Sport';
import TechnicalMetadataChecklist from '../../TechnicalMetadataChecklist';
import TechnicalMetadataWarnings from '../../TechnicalMetadataWarnings';
import { removeVideoAwaitingApprovalAtom } from '../../../atoms/videoListings';

const Container = styled.div`
  padding: ${themeHelper.spacing(0.25, 0)};
`;

const getDisabledReason = (
  intl,
  missingData,
  matchVideoAlreadyExists,
  userIsUploader,
) => {
  const missingDataTooltip = intl.formatMessage({
    defaultMessage: 'Data is incomplete.',
    id: 'ea9e1',
  });
  const videoExistsTooltip = intl.formatMessage({
    defaultMessage: 'Library entry already exists.',
    id: 'dc2f1',
  });
  const userIsUploaderTooltip = intl.formatMessage({
    defaultMessage: 'User who uploaded cannot approve.',
    id: 'd81fa',
  });

  const disabledReasons = [];
  if (missingData) {
    disabledReasons.push(missingDataTooltip);
  }
  if (matchVideoAlreadyExists) {
    disabledReasons.push(videoExistsTooltip);
  }
  if (userIsUploader) {
    disabledReasons.push(userIsUploaderTooltip);
  }

  return disabledReasons.join(' ');
};

const ModalContent = styled.div`
  min-width: 85vw;
`;

export function MoveVideo({
  matchId,
  matchVideoAlreadyExists,
  part,
  qualityAssuranceInfo,
  size,
  sourceData,
  sport,
  track,
  uploadedById,
  url,
  version,
  videoKey,
}) {
  const api = useApi();
  const intl = useIntl();

  const [src, setSrc] = useState();

  const [periodStartNotShown, setPeriodStartNotShown] = useState(false);
  const [periodEndNotShown, setPeriodEndNotShown] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  const [preventApproval, setPreventApproval] = useState(true);
  const [userIsUploader, setUserIsUploader] = useState(false);

  const [displayConfirmation, setDisplayConfirmation] = useState(false);

  const removeVideoAwaitingApproval = useSetAtom(
    removeVideoAwaitingApprovalAtom,
  );

  const metadataIsUpdating = useAtomValue(metadataIsUpdatingAtom);
  const setApprovalInProgress = useSetAtom(approvalInProgressAtom);

  const selectedSourceData = useAtomValue(selectedSourceDataAtom);
  const setIsEditingSourceData = useSetAtom(editingSourceDataAtom);
  const setIsEditingQAData = useSetAtom(editingQAChecklistAtom);
  const sourceDataIsUpdating = useAtomValue(sourceDataIsUpdatingAtom);
  const setAwaitingApprovalActionMessage = useSetAtom(
    messageAwaitingApprovalActionsAtom,
  );
  const [moveVideoMessage, setMoveVideoMessage] = useAtom(
    messageMoveVideoActionAtom,
  );

  const [videoInformationIsCorrect, setVideoInformationIsCorrect] =
    useState(false);
  const [videoQualityIsCorrect, setVideoQualityIsCorrect] = useState(false);

  const { refreshVideosAwaitingApproval } = useVideoListings();

  const videoReference = {
    matchId,
    matchPart: part,
    sport,
    track,
    version,
  };

  const clearSelectionsAndMessages = () => {
    setMoveVideoMessage();

    setPeriodEnd('');
    setPeriodStart('');
    setVideoInformationIsCorrect(false);
    setVideoQualityIsCorrect(false);
  };
  useEffect(() => {
    clearSelectionsAndMessages();
  }, [displayConfirmation]);

  useEffect(() => {
    setSrc(url);
  }, [videoKey]);

  const { user } = useKitbagAuth();
  useEffect(() => {
    if (user?.sub === uploadedById) {
      setUserIsUploader(true);
    }
  }, [user]);

  const MOVE_INITIATED_MESSAGE = intl.formatMessage({
    defaultMessage: 'Attempting to move video. Please wait',
    id: '8a57b',
  });
  const SUCCESS_MESSAGE = intl.formatMessage(
    {
      defaultMessage: '{videoKey} moved to the library',
      id: 'd9482',
    },
    { videoKey },
  );
  const FAILURE_MESSAGE = intl.formatMessage(
    {
      defaultMessage: 'Move {videoKey} failed. Please try again',
      description: 'Move video key failed',
      id: '60f47',
    },
    {
      videoKey,
    },
  );

  const moveVideo = async () => {
    setMoveVideoMessage(MOVE_INITIATED_MESSAGE);
    const data = JSON.stringify({
      match_id: matchId,
      part,
      period_end: periodEndNotShown
        ? VALUE_IDENTIFYING_NOT_SHOWN
        : timestampAsMicroseconds(periodEnd, { includesHours: true }),
      period_start: periodStartNotShown
        ? VALUE_IDENTIFYING_NOT_SHOWN
        : timestampAsMicroseconds(periodStart, { includesHours: true }),
      ...sourceSerialiser(sourceData),
      sport,
      track,
      version,
    });

    setApprovalInProgress(true);
    const response = await api.put(
      `${API_ENDPOINTS.videoService}/videos/${videoKey}`,
      {
        data,
      },
    );
    setApprovalInProgress(false);

    if (response) {
      setDisplayConfirmation(false);
      setMoveVideoMessage();
      removeVideoAwaitingApproval(videoKey);
    }
    setAwaitingApprovalActionMessage(
      response ? SUCCESS_MESSAGE : FAILURE_MESSAGE,
    );
  };

  const handleMoveClick = () => setDisplayConfirmation(true);

  const handleCancelClick = () => {
    setDisplayConfirmation(false);
    setPreventApproval(false);
    setIsEditingSourceData(false);
    setIsEditingQAData(false);
  };

  const confirmationTitle = intl.formatMessage(
    {
      defaultMessage: 'Approve Video {videoKey}',
      id: '48442',
    },
    { videoKey },
  );

  const missingData = !(matchId && part);
  const disabled = missingData || matchVideoAlreadyExists || userIsUploader;

  const defaultTooltip = intl.formatMessage(
    {
      defaultMessage: 'Move {videoKey} to library',
      id: '55ce7',
    },
    { videoKey },
  );

  const disabledReason = getDisabledReason(
    intl,
    missingData,
    matchVideoAlreadyExists,
    userIsUploader,
  );

  const isFootballVideo = sport === FOOTBALL_IDENTIFIER;

  const offsetSelectionIsValid =
    (periodStartNotShown && periodEndNotShown) ||
    (periodStartNotShown && periodEnd) ||
    (periodEndNotShown && periodStart) ||
    (periodStart && periodEnd && periodEnd > periodStart && isFootballVideo) ||
    !isFootballVideo;

  const isDisabledConfirm =
    api.isFetching ||
    preventApproval ||
    !videoQualityIsCorrect ||
    !videoInformationIsCorrect ||
    !offsetSelectionIsValid ||
    disabled ||
    metadataIsUpdating ||
    sourceDataIsUpdating;

  const allowConfirmation = () => {
    setPreventApproval(false);
  };
  const preventConfirmation = () => {
    setPreventApproval(true);
  };

  const onSourceDataSave = async () => {
    await api.patch(`${API_ENDPOINTS.videoService}/videos/${videoKey}`, {
      data: JSON.stringify({
        ...sourceSerialiser(selectedSourceData),
      }),
    });
    await refreshVideosAwaitingApproval();
    setIsEditingSourceData(false);
  };

  return (
    <>
      <ButtonIcon
        icon="TickDone"
        onClick={handleMoveClick}
        size={size}
        title={disabled ? disabledReason : defaultTooltip}
        variant="secondary"
      />
      {displayConfirmation && (
        <Confirm
          id={`videoaction.move.${videoKey}`}
          isOpen={displayConfirmation}
          isDisabledConfirm={isDisabledConfirm}
          onCancel={handleCancelClick}
          onConfirm={moveVideo}
          title={confirmationTitle}
        >
          {displayConfirmation && (
            <ModalContent>
              <TechnicalMetadataWarnings />
              {moveVideoMessage && <Alert>{moveVideoMessage}</Alert>}
              {disabled && <Alert type="error">{disabledReason}</Alert>}
              <Grid>
                <Grid item xs={12} lg={8}>
                  <Container>
                    {/* Disabled as known prefix for dev only internal components */}
                    {/* eslint-disable-next-line react/jsx-pascal-case */}
                    <_VideoPlayer
                      autoPlay
                      src={src}
                      testId={`preview-${src}`}
                    />
                  </Container>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Container>
                    <QAChecklist
                      editable
                      videoKey={videoKey}
                      videoReference={videoReference}
                    />
                  </Container>
                  <Container>
                    <SourceData
                      editable
                      lastUpdatedByIsHidden
                      onSave={onSourceDataSave}
                      sourceData={sourceData}
                      sport={sport}
                    />
                  </Container>
                  {isFootballVideo && (
                    <Container>
                      <OffsetData
                        alwaysInEditMode
                        lastUpdatedByIsHidden
                        periodStartNotShown={periodStartNotShown}
                        periodStart={periodStart}
                        periodEnd={periodEnd}
                        periodEndNotShown={periodEndNotShown}
                        setPeriodStartNotShown={setPeriodStartNotShown}
                        setPeriodEnd={setPeriodEnd}
                        setPeriodEndNotShown={setPeriodEndNotShown}
                        setPeriodStart={setPeriodStart}
                        videoReference={videoReference}
                      />
                    </Container>
                  )}
                  <Container>
                    <QAInfoView
                      hideApprover
                      qualityAssuranceInfo={qualityAssuranceInfo}
                    />
                  </Container>
                  <Container>
                    <TechnicalMetadataChecklist
                      isSelectedForDisplay={displayConfirmation}
                      onLoading={preventConfirmation}
                      onLoadedWithoutBlockingErrors={allowConfirmation}
                      sport={sport}
                      videoKey={videoKey}
                    />
                  </Container>
                  <Container>
                    <QACheckConfirmation
                      preventApproval={preventApproval}
                      setVideoInformationIsCorrect={
                        setVideoInformationIsCorrect
                      }
                      setVideoQualityIsCorrect={setVideoQualityIsCorrect}
                      videoInformationIsCorrect={videoInformationIsCorrect}
                      videoQualityIsCorrect={videoQualityIsCorrect}
                    />
                  </Container>
                </Grid>
              </Grid>
            </ModalContent>
          )}
        </Confirm>
      )}
    </>
  );
}

MoveVideo.propTypes = {
  matchId: PropTypes.string,
  matchVideoAlreadyExists: PropTypes.bool,
  part: PropTypes.string,
  qualityAssuranceInfo: PropTypes.shape(qaDataPropTypes).isRequired,
  size: PropTypes.string,
  sourceData: PropTypes.shape({
    isLicensed: PropTypes.string,
    sourceName: PropTypes.string,
    sourceType: PropTypes.string,
    uploadMethod: PropTypes.string,
  }).isRequired,
  sport: PropTypes.string,
  track: PropTypes.string,
  uploadedById: PropTypes.string.isRequired,
  url: PropTypes.string,
  version: PropTypes.string,
  videoKey: PropTypes.string.isRequired,
};

MoveVideo.defaultProps = {
  matchId: undefined,
  matchVideoAlreadyExists: false,
  part: undefined,
  size: undefined,
  sport: undefined,
  track: undefined,
  url: undefined,
  version: undefined,
};

export default { MoveVideo };
